import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Check, CheckCircle, AlertTriangle, ArrowLeft, RefreshCw, Wifi, WifiOff, Trash2, ExternalLink, RotateCcw, Clock, Send, Copy, FileDown, Usb, Download, Camera, Upload } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import {
  getCountPackage,
  syncCounts,
  removeCountPackage,
  downloadCountPackage,
  COUNT_SYNC_STATES,
} from '../../services/countOfflineService';
import {
  getLocalObservations,
  getQueuedPhotos,
} from '../../services/observationsOfflineService';

export default function CountSyncPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const sessionId = id ? parseInt(id, 10) : null;

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [failedEntries, setFailedEntries] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const loadPackage = useCallback(async () => {
    if (!sessionId) {
      setLoading(false);
      return;
    }
    const stored = await getCountPackage(sessionId);
    if (!stored) {
      toast.error('Count package not found');
      navigate('/count/sessions');
      return;
    }
    setPkg(stored);
    setLoading(false);
  }, [sessionId, navigate]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  const handleSync = async (syncOptions = {}) => {
    setSyncing(true);
    setSyncResult(null);
    const result = await syncCounts(sessionId, syncOptions);
    setSyncing(false);
    setSyncResult(result);

    if (result.success) {
      toast.success(`Synced ${result.synced} count entries${result.skipped ? ` (${result.skipped} skipped)` : ''}`);
      await loadPackage();
    } else if (result.conflict) {
      // Don't show toast — the conflict UI will appear in-page
    } else {
      toast.error(`Sync failed: ${result.error}`);
    }
  };

  const handleForceOverwrite = () => handleSync({ forceOverwrite: true });
  const handleUncountedOnly = () => handleSync({ uncountedOnly: true, forceOverwrite: true });
  const handleRetryFailed = () => handleSync({ forceOverwrite: true });

  const handleRedownload = async () => {
    if (!window.confirm(
      'Re-download will REPLACE all your local count data for this session with the latest from the server.\n\n' +
      'Any unsynced local counts will be lost.\n\nProceed?'
    )) return;
    setSyncing(true);
    await removeCountPackage(sessionId);
    const result = await downloadCountPackage(sessionId);
    setSyncing(false);
    if (result.success) {
      toast.success('Package re-downloaded from server');
      await loadPackage();
    } else {
      toast.error('Re-download failed: ' + (result.error || 'Unknown error'));
      navigate('/count/sessions');
    }
  };

  const handleDiscard = async () => {
    if (window.confirm('Discard all local counts for this session? This cannot be undone.')) {
      await removeCountPackage(sessionId);
      toast.info('Local counts discarded');
      navigate('/count/sessions');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!pkg && !sessionId) {
    // Global sync page — list all packages needing sync
    return <GlobalSyncView />;
  }

  if (!pkg) return null;

  const localCounts = pkg.local_counts || {};
  const countedEntries = Object.entries(localCounts).filter(([, v]) => v.quantity != null);
  const totalLines = pkg.package?.count_sheet_lines?.length || 0;
  const syncState = pkg.sync_state;
  const isSynced = syncState === COUNT_SYNC_STATES.SYNCED;
  const isPartial = syncState === COUNT_SYNC_STATES.PARTIAL;

  return (
    <div className="p-4 max-w-lg mx-auto" data-testid="count-sync-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/count/sessions/${sessionId}`)}
          className="p-1 rounded-lg hover:bg-slate-100 text-slate-400"
          data-testid="sync-back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-xl font-bold text-slate-900">Finish & Sync</h1>
      </div>

      {/* Summary card */}
      <Card className="p-5 mb-4" data-testid="sync-summary-card">
        <h2 className="font-semibold text-slate-800 mb-3">Count Summary</h2>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-500">Session</span>
            <span className="font-medium">{pkg.session_reference}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Site</span>
            <span className="font-medium">{pkg.site_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Products counted</span>
            <span className="font-medium" data-testid="sync-counted-total">{countedEntries.length} / {totalLines}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Status</span>
            <span className={`font-medium ${isSynced ? 'text-green-600' : isPartial ? 'text-amber-600' : 'text-amber-600'}`} data-testid="sync-status">
              {isSynced ? 'Synced' : isPartial ? 'Partial — some failed' : syncState === COUNT_SYNC_STATES.CONFLICT ? 'Conflict' : 'Pending sync'}
            </span>
          </div>
          {pkg.last_synced_at && (
            <div className="flex justify-between">
              <span className="text-slate-500">Last synced</span>
              <span className="font-medium text-slate-600 text-xs">
                {new Date(pkg.last_synced_at).toLocaleString()}
              </span>
            </div>
          )}
        </div>
      </Card>

      {/* Connection status */}
      <div className={`flex items-center gap-2 mb-4 text-sm ${isOnline ? 'text-green-600' : 'text-amber-600'}`}>
        {isOnline ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
        {isOnline ? 'Connected — ready to sync' : 'Offline — connect to sync'}
      </div>

      {/* Sync result */}
      {syncResult && (
        <Card className={`p-4 mb-4 ${syncResult.success && !syncResult.partial ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} data-testid="sync-result">
          <div className="flex items-start gap-2">
            {syncResult.success && !syncResult.partial ? (
              <Check className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              {syncResult.success && !syncResult.partial ? (
                <p className="text-sm text-green-800">
                  Successfully synced {syncResult.synced} entries.
                  {syncResult.skipped > 0 && ` (${syncResult.skipped} skipped)`}
                </p>
              ) : syncResult.partial ? (
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Synced {syncResult.synced} entries, {syncResult.failures.length} failed
                  </p>
                  <ul className="mt-2 space-y-1">
                    {syncResult.failures.map((f, i) => (
                      <li key={i} className="text-xs text-red-600">
                        Line #{f.count_sheet_line_id}: {f.error}
                      </li>
                    ))}
                  </ul>
                </div>
              ) : syncResult.conflict ? (
                <div>
                  <p className="text-sm font-medium text-amber-800">Conflict: Session modified on server</p>
                  <p className="text-xs text-slate-600 mt-1 mb-3">
                    Someone updated this session on the server since you downloaded it. Choose how to proceed:
                  </p>
                  <div className="space-y-2">
                    <Button
                      size="sm"
                      className="w-full justify-start h-auto py-2.5 bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={handleForceOverwrite}
                      disabled={syncing}
                      data-testid="conflict-overwrite-all-btn"
                    >
                      <Upload className="w-4 h-4 mr-2 shrink-0" />
                      <div className="text-left">
                        <span className="text-xs font-medium block">Overwrite all on server</span>
                        <span className="text-[10px] opacity-80">Replace all server counts with your local data</span>
                      </div>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full justify-start h-auto py-2.5"
                      onClick={handleUncountedOnly}
                      disabled={syncing}
                      data-testid="conflict-uncounted-only-btn"
                    >
                      <Send className="w-4 h-4 mr-2 shrink-0" />
                      <div className="text-left">
                        <span className="text-xs font-medium block">Fill uncounted items only</span>
                        <span className="text-[10px] text-muted-foreground">Only send counts for items not yet counted on server</span>
                      </div>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full justify-start h-auto py-2.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                      onClick={handleRedownload}
                      disabled={syncing}
                      data-testid="conflict-redownload-btn"
                    >
                      <Download className="w-4 h-4 mr-2 shrink-0" />
                      <div className="text-left">
                        <span className="text-xs font-medium block">Re-download from server</span>
                        <span className="text-[10px] text-muted-foreground">Discard local counts, get latest from server</span>
                      </div>
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-red-800">Sync failed: {syncResult.error}</p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Actions */}
      <div className="space-y-3">
        <Button
          className={`w-full h-12 text-base ${isSynced ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          onClick={() => handleSync()}
          disabled={!isOnline || syncing || countedEntries.length === 0}
          data-testid="sync-now-btn"
        >
          {syncing ? (
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
          ) : isSynced ? (
            <RefreshCw className="w-5 h-5 mr-2" />
          ) : (
            <Send className="w-5 h-5 mr-2" />
          )}
          {syncing ? 'Syncing...' : isSynced ? 'Re-sync Counts' : 'Sync Now'}
        </Button>

        {isPartial && failedEntries.length > 0 && (
          <Button
            className="w-full h-12 text-base bg-amber-600 hover:bg-amber-700"
            onClick={handleRetryFailed}
            disabled={!isOnline || syncing}
            data-testid="sync-retry-failed-btn"
          >
            {syncing ? (
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
            ) : (
              <RotateCcw className="w-5 h-5 mr-2" />
            )}
            {syncing ? 'Retrying...' : `Retry ${failedEntries.length} Failed`}
          </Button>
        )}

        {(isSynced || isPartial) && (
          <>
            <Button
              variant="outline"
              className="w-full h-12 text-base"
              onClick={() => navigate('/count/sessions')}
              data-testid="sync-return-to-sessions-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              Back to Sessions
            </Button>
          </>
        )}

        {/* Observations — review, add, or edit observations and photos */}
        <Button
          variant="outline"
          className="w-full h-11 text-base"
          onClick={() => navigate(`/count/sessions/${sessionId}/observations`)}
          data-testid="sync-observations-btn"
        >
          <Camera className="w-4 h-4 mr-2" />
          View / Edit Observations
        </Button>

        {/* Re-download from server */}
        {isOnline && (
          <Button
            variant="outline"
            className="w-full h-11 text-amber-700 border-amber-300 hover:bg-amber-50"
            onClick={handleRedownload}
            disabled={syncing}
            data-testid="sync-redownload-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Re-download from Server
          </Button>
        )}

        <Button
          variant="ghost"
          className="w-full text-red-500 hover:text-red-700 hover:bg-red-50"
          onClick={handleDiscard}
          data-testid="sync-discard-btn"
        >
          <Trash2 className="w-4 h-4 mr-2" />
          Discard Local Counts
        </Button>
      </div>

      {/* Device-to-device transfer — always available (works offline) */}
      {countedEntries.length > 0 && (
        <DeviceTransferSection pkg={pkg} sessionId={sessionId} />
      )}

      {/* Sync audit log */}
      <SyncLogSection sessionId={sessionId} isOnline={isOnline} />
    </div>
  );
}

/**
 * Device-to-device transfer — export count data as JSON file or copy to clipboard.
 * Works fully offline. The receiving device imports via the Transfer page.
 */
function DeviceTransferSection({ pkg, sessionId }) {
  const [exporting, setExporting] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  const buildTransferPayload = async () => {
    const localCounts = pkg.local_counts || {};
    const lines = (pkg.package?.count_sheet_lines || []).map(line => {
      const lc = localCounts[line.id];
      return {
        count_sheet_line_id: line.id,
        beverage_product_id: line.beverage_product_id,
        product_name: line.product_name,
        quantity: lc?.quantity ?? null,
        unit_id: lc?.unit_id ?? null,
        unit_label: lc?.unit_label ?? null,
        notes: lc?.notes ?? null,
      };
    }).filter(l => l.quantity != null);

    // Collect observations for this session
    const observations = await getLocalObservations(sessionId);
    const observationsWithPhotos = [];
    for (const obs of observations) {
      const photos = await getQueuedPhotos(obs.local_id);
      const photoData = [];
      for (const p of photos) {
        // Convert ArrayBuffer to base64 for JSON serialization
        let base64 = null;
        if (p.blob) {
          const bytes = new Uint8Array(p.blob);
          let binary = '';
          for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
          base64 = btoa(binary);
        }
        photoData.push({
          local_id: p.local_id,
          file_name: p.file_name,
          mime_type: p.mime_type,
          caption: p.caption,
          data_base64: base64,
        });
      }
      observationsWithPhotos.push({
        local_id: obs.local_id,
        note_text: obs.note_text,
        category: obs.category,
        severity: obs.severity,
        product_id: obs.product_id,
        product_name_snapshot: obs.product_name_snapshot,
        location_id: obs.location_id,
        location_name_snapshot: obs.location_name_snapshot,
        created_at: obs.created_at,
        photos: photoData,
      });
    }

    return {
      transfer_type: 'count_data',
      session_id: sessionId,
      session_reference: pkg.session_reference,
      site_name: pkg.site_name,
      counted_lines: lines,
      observations: observationsWithPhotos,
      exported_at: new Date().toISOString(),
      device_info: navigator.userAgent?.substring(0, 60) || 'unknown',
    };
  };

  const handleFileExport = async () => {
    setExporting(true);
    try {
      const payload = await buildTransferPayload();
      const obsCount = (payload.observations || []).length;
      const photoCount = (payload.observations || []).reduce((s, o) => s + (o.photos || []).length, 0);
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `count_${pkg.session_reference}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const parts = [`${payload.counted_lines.length} count lines`];
      if (obsCount > 0) parts.push(`${obsCount} observations`);
      if (photoCount > 0) parts.push(`${photoCount} photos`);
      toast.success(`Saved: ${parts.join(', ')}`);
      setShowSteps(true);
    } catch (err) {
      toast.error('Export failed: ' + err.message);
    }
    setExporting(false);
  };

  const handleCopyToClipboard = async () => {
    try {
      const payload = await buildTransferPayload();
      await navigator.clipboard.writeText(JSON.stringify(payload));
      toast.success('Count data copied — paste on receiving device');
    } catch (err) {
      toast.error('Copy failed: ' + err.message);
    }
  };

  return (
    <div className="mt-6 border-t border-slate-100 pt-4" data-testid="device-transfer-section">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
        <Send className="w-4 h-4" />
        Transfer to Computer
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        Export your count data, then transfer the file to your computer via USB cable.
      </p>

      {/* Step 1: Export */}
      <div className="space-y-2 mb-4">
        <Button
          variant="outline"
          className="w-full h-auto py-3 justify-start gap-3"
          onClick={handleFileExport}
          disabled={exporting}
          data-testid="sync-file-export-btn"
        >
          <div className="bg-teal-100 p-1.5 rounded-lg shrink-0">
            <FileDown className="h-4 w-4 text-teal-700" />
          </div>
          <div className="text-left">
            <span className="text-sm font-medium block">Step 1: Save Count File</span>
            <span className="text-[10px] text-muted-foreground">Saves a .json file to your Downloads folder</span>
          </div>
        </Button>

        <Button
          variant="ghost"
          className="w-full text-xs text-slate-500"
          onClick={handleCopyToClipboard}
          data-testid="sync-copy-clipboard-btn"
        >
          <Copy className="h-3 w-3 mr-1.5" />
          Alternative: Copy to clipboard
        </Button>
      </div>

      {/* Step 2-4: USB transfer instructions */}
      {showSteps && (
        <div className="bg-slate-50 rounded-lg p-3 space-y-3 text-xs" data-testid="usb-transfer-steps">
          <div className="flex items-start gap-2.5">
            <span className="bg-slate-200 text-slate-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">2</span>
            <div>
              <p className="font-medium text-slate-700">Connect tablet to computer via USB cable</p>
              <p className="text-muted-foreground mt-0.5">Use a USB-C or micro-USB cable. Select "File Transfer" if prompted on the tablet.</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <span className="bg-slate-200 text-slate-600 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">3</span>
            <div>
              <p className="font-medium text-slate-700">Copy the file to your computer</p>
              <p className="text-muted-foreground mt-0.5">
                <strong>Mac:</strong> Open Android File Transfer, navigate to Downloads, drag the .json file to your Desktop.<br/>
                <strong>Windows:</strong> Open File Explorer, find the tablet under devices, navigate to Downloads.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <span className="bg-emerald-200 text-emerald-700 rounded-full w-5 h-5 flex items-center justify-center text-[10px] font-bold shrink-0 mt-0.5">4</span>
            <div>
              <p className="font-medium text-slate-700">Import in AbbeyZone wizard</p>
              <p className="text-muted-foreground mt-0.5">On the Closing Stock step, click "Import Count File" and select the .json file.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Sync audit log — shows server-side sync history for this session.
 */
function SyncLogSection({ sessionId, isOnline }) {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!sessionId || !isOnline || !expanded) return;
    setLoadingLogs(true);
    api.get(`/reconciliation/sessions/${sessionId}/sync-log`)
      .then(res => setLogs(res.data.sync_logs || []))
      .catch(() => {})
      .finally(() => setLoadingLogs(false));
  }, [sessionId, isOnline, expanded]);

  return (
    <div className="mt-6 border-t border-slate-100 pt-4" data-testid="sync-log-section">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700 w-full"
        data-testid="sync-log-toggle"
      >
        <Clock className="w-4 h-4" />
        <span className="font-medium">Sync History</span>
        {expanded ? '▲' : '▼'}
      </button>
      {expanded && (
        <div className="mt-3 space-y-2">
          {loadingLogs ? (
            <div className="text-center py-4"><Loader2 className="w-4 h-4 animate-spin text-slate-300 inline" /></div>
          ) : logs.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-3">No sync events recorded</p>
          ) : (
            logs.map(log => (
              <Card key={log.id} className="p-3 text-sm" data-testid={`sync-log-entry-${log.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">{log.user_name}</span>
                  <span className="text-xs text-slate-400">{new Date(log.created_at).toLocaleString()}</span>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-xs px-1.5 py-0.5 rounded ${
                    log.action === 'full_sync' ? 'bg-green-100 text-green-700' :
                    log.action === 'partial_sync' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>{log.action.replace('_', ' ')}</span>
                  <span className="text-xs text-slate-500">{log.entries_synced} synced</span>
                  {log.entries_failed > 0 && (
                    <span className="text-xs text-red-500">{log.entries_failed} failed</span>
                  )}
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Global sync view — shown when navigating to /count/sync without a session ID.
 * Lists all local count packages and their sync states.
 */
function GlobalSyncView() {
  const navigate = useNavigate();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const { getAllCountPackages } = await import('../../services/countOfflineService');
      const pkgs = await getAllCountPackages();
      setPackages(pkgs);
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="p-4 max-w-2xl mx-auto" data-testid="global-sync-page">
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Sync Status</h1>

      {packages.length === 0 ? (
        <div className="text-center py-16 text-slate-400">
          <p>No local count packages</p>
        </div>
      ) : (
        <div className="space-y-3">
          {packages.map(pkg => {
            const counted = Object.keys(pkg.local_counts || {}).filter(
              k => pkg.local_counts[k]?.quantity != null
            ).length;
            const total = pkg.package?.count_sheet_lines?.length || 0;
            const isSynced = pkg.sync_state === COUNT_SYNC_STATES.SYNCED;

            return (
              <Card
                key={pkg.id}
                className="p-4 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => navigate(`/count/sessions/${pkg.id}/sync`)}
                data-testid={`global-sync-card-${pkg.id}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-slate-800">{pkg.site_name}</p>
                    <p className="text-sm text-slate-500">{pkg.session_reference} &middot; {counted}/{total} counted</p>
                  </div>
                  <div className={`text-sm font-medium ${isSynced ? 'text-green-600' : 'text-amber-600'}`}>
                    {isSynced ? 'Synced' : pkg.sync_state === COUNT_SYNC_STATES.CONFLICT ? 'Conflict' : 'Pending'}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
